import { useQuery } from '@apollo/client';
import { Order_By } from '@openx/types/gqlTypes';
import { useMemo } from 'react';
import { GetLalSegmentsBuildAudienceDocument } from '../../../../../../graphql/query/audiences/GetLalSegmentsBuildAudience.generated';
import type { LookalikeSegmentDefinition } from '../../../types';
import { constructSegmentFilterParams } from '../components/drawer/components/SegmentsList/utils/constructSegmentFilterParams';
import type { UseSegments } from './types';
import { buildLookalikeFilters } from './utils/lookalikeFilterBuilder';
import { transformLalDefinitions } from './utils/transformLalDefinitions';

export const useGetLalSegments: UseSegments<LookalikeSegmentDefinition> = ({
  accountId = '',
  linkedProviders,
  limit = 50,
  filters,
}) => {
  const segmentParams = useMemo(
    () => constructSegmentFilterParams({ filters, searchPhrase: filters.phrase || '' }),
    [filters]
  );
  const { where } = useMemo(
    () => buildLookalikeFilters({ filters: segmentParams, accountId, linkedProviders }),
    [accountId, linkedProviders, segmentParams]
  );

  const {
    data: lalSegmentsData,
    loading,
    error,
    refetch,
  } = useQuery(GetLalSegmentsBuildAudienceDocument, {
    variables: {
      where,
      limit: filters.limit ?? limit,
      offset: segmentParams.offset ?? 0,
      order_by: { updated_date: Order_By.Desc },
    },
    notifyOnNetworkStatusChange: true,
  });

  const transformedDefinitions: LookalikeSegmentDefinition[] = useMemo(
    () => transformLalDefinitions(accountId, lalSegmentsData?.lalSegmentFeed),
    [lalSegmentsData?.lalSegmentFeed, accountId]
  );

  return {
    loading,
    error,
    segments: transformedDefinitions,
    totalCount: lalSegmentsData?.lookalike_definition_aggregate?.aggregate?.count ?? 0,
    refetch,
  };
};
